/**
 * 水印/认证状态枚举与工具函数
 * 
 * v2 变更说明：
 * - 状态值从 0-4 扩展为 0-5，新增状态5（已签署且关闭水印）
 * - 数据源从独立的 CertifyResultData 统一为 userInfo.realizeResult
 * - 废弃旧的 RealizeResultEnum (0-4)，使用新的 WatermarkStatusEnum (0-5)
 */

// ==================== 新枚举定义（v2）====================

/** 水印/认证状态枚举 (0-5)，直接对应后端 userInfo.realizeResult 字段 */
export enum WatermarkStatusEnum {
  /** 0 - 待认证：用户尚未开始任何流程 */
  WAITING = 0,
  /** 1 - 认证处理中：支付宝扫码认证进行中 */
  PROCESSING = 1,
  /** 2 - 已认证：身份验证通过，但尚未同意协议 */
  CERTIFIED = 2,
  /** 3 - 认证失败：身份验证不通过，可重新尝试 */
  FAILED = 3,
  /** 4 - 已签署（未关闭水印）：协议已同意，Toggle=OFF，水印显示中 */
  SIGNED_WATERMARK_ON = 4,
  /** 5 - 已签署（已关闭水印）：协议已同意，Toggle=ON，水印已去除 */
  SIGNED_WATERMARK_OFF = 5,
}

/** 状态中文描述映射表 */
export const WatermarkStatusDescMap: Record<WatermarkStatusEnum, string> = {
  [WatermarkStatusEnum.WAITING]: '待认证',
  [WatermarkStatusEnum.PROCESSING]: '认证处理中',
  [WatermarkStatusEnum.CERTIFIED]: '已认证',
  [WatermarkStatusEnum.FAILED]: '认证失败',
  [WatermarkStatusEnum.SIGNED_WATERMARK_ON]: '已签署',
  [WatermarkStatusEnum.SIGNED_WATERMARK_OFF]: '已去水印',
};

// ==================== UI状态推导函数 ====================

/**
 * 从 realizeResult 数值推导完整的UI展示属性
 * 
 * 这是核心映射函数，替代旧的多字段 watermarkState 方案。
 * 所有组件应通过此函数将后端数值转为前端UI属性。
 */
export interface DerivedWatermarkState {
  /** 是否已同意协议 (rr >= 4) */
  hasAgreedProtocol: boolean;
  /** 是否关闭了水印 (rr === 5) */
  watermarkRemoved: boolean;
  /** 是否可以操作 Toggle 开关 (rr >= 4 && 是会员) */
  canToggleSwitch: boolean;
  /** 状态描述文字 */
  statusText: string;
  /** 状态文字颜色 */
  statusColor: string;
  /** Toggle 开关值: null=隐藏, false=OFF(关), true=ON(开) */
  toggleValue: boolean | null;
}

export function deriveWatermarkState(rr: number): DerivedWatermarkState {
  const stateMap: Record<number, DerivedWatermarkState> = {
    [WatermarkStatusEnum.WAITING]: {
      hasAgreedProtocol: false,
      watermarkRemoved: false,
      canToggleSwitch: false,
      statusText: '当前状态：未开启去除水印',
      statusColor: '#666666',
      toggleValue: false,
    },
    [WatermarkStatusEnum.PROCESSING]: {
      hasAgreedProtocol: false,
      watermarkRemoved: false,
      canToggleSwitch: false,
      statusText: '当前状态：未开启去除水印',
      statusColor: '#666666',
      toggleValue: false,
    },
    [WatermarkStatusEnum.CERTIFIED]: {
      hasAgreedProtocol: false,
      watermarkRemoved: false,
      canToggleSwitch: false,
      statusText: '当前状态：未开启去除水印',
      statusColor: '#666666',
      toggleValue: false, // 显示但禁用态
    },
    [WatermarkStatusEnum.FAILED]: {
      hasAgreedProtocol: false,
      watermarkRemoved: false,
      canToggleSwitch: false,
      statusText: '当前状态：未开启去除水印',
      statusColor: '#666666',
      toggleValue: false,
    },
    [WatermarkStatusEnum.SIGNED_WATERMARK_ON]: {
      hasAgreedProtocol: true,
      watermarkRemoved: false,
      canToggleSwitch: true,
      statusText: '当前状态：未开启去除水印',
      statusColor: '#666666',
      toggleValue: false,
    },
    [WatermarkStatusEnum.SIGNED_WATERMARK_OFF]: {
      hasAgreedProtocol: true,
      watermarkRemoved: true,
      canToggleSwitch: true,
      statusText: '当前状态：已开启去除水印',
      statusColor: '#8C5BEC',
      toggleValue: true,
    },
  };
  
  // 未知或超范围值降级为 0（待认证）
  return stateMap[rr] ?? stateMap[WatermarkStatusEnum.WAITING];
}
